import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box, Button, Card, CardContent, Chip, CircularProgress, FormControl, InputLabel, Link,
  MenuItem, Select, Step, StepLabel, Stepper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, TextField, Typography, makeStyles,
} from '@material-ui/core';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import CloudUploadIcon from '@material-ui/icons/CloudUpload';
import DoneIcon from '@material-ui/icons/Done';
import RefreshIcon from '@material-ui/icons/Refresh';
import * as XLSX from 'xlsx';
import {
  autoMapCommercialColumns,
  buildCommercialImportRequest,
  commercialImportDatasets,
  deferredCommercialImports,
  importDataset,
  mappedCommercialRows,
  missingRequiredImportFields,
  parseCommercialSheetMatrix,
} from './commercialImportState';

const steps = ['1. Choose file', '2. Map columns', '3. Review changes'];

const useStyles = makeStyles(theme => ({
  workflow: { minWidth: 0, overflow: 'hidden' },
  stepper: { background: 'transparent', padding: theme.spacing(1, 0, 3) },
  section: { paddingTop: theme.spacing(1) },
  controls: { alignItems: 'center', display: 'flex', flexWrap: 'wrap', gap: theme.spacing(1.5) },
  mappingGrid: {
    display: 'grid', gap: theme.spacing(2), gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
    marginBottom: theme.spacing(2),
  },
  notice: {
    background: theme.palette.action.hover, borderLeft: `4px solid ${theme.palette.primary.main}`,
    marginBottom: theme.spacing(2), padding: theme.spacing(1.5, 2),
  },
  errorNotice: { borderLeftColor: theme.palette.error.main },
  successNotice: { borderLeftColor: theme.palette.success.main },
  warningNotice: { borderLeftColor: theme.palette.warning.main },
  table: { marginTop: theme.spacing(2), maxHeight: 360, overflowX: 'auto' },
  dataTable: { minWidth: 720 },
  actions: { alignItems: 'center', display: 'flex', flexWrap: 'wrap', gap: theme.spacing(1), marginTop: theme.spacing(2) },
  summary: { display: 'flex', flexWrap: 'wrap', gap: theme.spacing(1), marginTop: theme.spacing(1) },
  supporting: { marginTop: theme.spacing(3) },
  deferredList: { display: 'grid', gap: theme.spacing(1.5), marginTop: theme.spacing(1.5) },
  deferredItem: { borderBottom: `1px solid ${theme.palette.divider}`, paddingBottom: theme.spacing(1.5) },
}));

const responseError = async (response, fallback) => {
  const body = await response.json().catch(() => ({}));
  const message = Array.isArray(body.message) ? body.message.join('. ') : body.message;
  return new Error(message || `${fallback} (${response.status})`);
};

const SummaryChips = ({ summary = {} }) => <Box className={useStyles().summary} aria-label="Import totals">
  <Chip size="small" label={`${Number(summary.created || 0)} created`} />
  <Chip size="small" label={`${Number(summary.updated || 0)} updated`} />
  <Chip size="small" label={`${Number(summary.skipped || 0)} skipped`} />
  <Chip size="small" color={Number(summary.failed || 0) ? 'secondary' : 'default'} label={`${Number(summary.failed || 0)} failed`} />
</Box>;

const IssueTable = ({ issues = [] }) => {
  if (!issues.length) return <Typography variant="body2" color="textSecondary">No validation issues.</Typography>;
  return <TableContainer style={{ marginTop: 12, maxHeight: 260 }}>
    <Table size="small" stickyHeader aria-label="Import validation issues">
      <caption>Rows that need attention before or after this import.</caption>
      <TableHead><TableRow><TableCell>Row</TableCell><TableCell>Issue</TableCell><TableCell>What to do</TableCell></TableRow></TableHead>
      <TableBody>{issues.map((issue, index) => <TableRow key={`${issue.code}-${issue.row}-${index}`}>
        <TableCell>{issue.row || 'File'}</TableCell>
        <TableCell><Chip size="small" variant="outlined" color={issue.severity === 'ERROR' ? 'secondary' : 'default'} label={String(issue.code || 'ISSUE').replace(/_/g, ' ')} /></TableCell>
        <TableCell>{issue.message}</TableCell>
      </TableRow>)}</TableBody>
    </Table>
  </TableContainer>;
};

export default function CommercialImportWorkflow({ headers, hostname, onImported }) {
  const classes = useStyles();
  const [step, setStep] = useState(0);
  const [datasetCode, setDatasetCode] = useState('CUSTOMERS');
  const [workbook, setWorkbook] = useState(null);
  const [filename, setFilename] = useState('');
  const [sheetNames, setSheetNames] = useState([]);
  const [sheet, setSheet] = useState('');
  const [matrix, setMatrix] = useState([]);
  const [headerRow, setHeaderRow] = useState(1);
  const [detectedHeaderRow, setDetectedHeaderRow] = useState(1);
  const [columns, setColumns] = useState([]);
  const [rows, setRows] = useState([]);
  const [mapping, setMapping] = useState({});
  const [preview, setPreview] = useState(null);
  const [execution, setExecution] = useState(null);
  const [recent, setRecent] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const dataset = importDataset(datasetCode);
  const missing = missingRequiredImportFields(datasetCode, mapping);
  const mappedRows = useMemo(() => mappedCommercialRows(rows, mapping, headerRow), [headerRow, mapping, rows]);
  const previewFailedRows = Number(preview?.summary?.failed || 0);
  const previewValidRows = Math.max(0, Number(preview?.totalRows || 0) - previewFailedRows);
  const partialPreview = Boolean(preview?.canExecute && previewFailedRows > 0);

  const loadRecent = useCallback(() => fetch(`${hostname}/commercial/imports/recent?limit=10`, { headers })
    .then(response => response.ok ? response.json() : Promise.reject(response))
    .then(setRecent)
    .catch(() => setRecent([])), [headers, hostname]);

  useEffect(() => { loadRecent(); }, [loadRecent]);

  const selectSheet = (book, name, nextDataset = datasetCode) => {
    const nextMatrix = XLSX.utils.sheet_to_json(book.Sheets[name], { header: 1, defval: '', raw: true, blankrows: false });
    const parsed = parseCommercialSheetMatrix(nextMatrix, nextDataset);
    setSheet(name);
    setMatrix(nextMatrix);
    setHeaderRow(parsed.headerRow);
    setDetectedHeaderRow(parsed.detectedHeaderRow);
    setRows(parsed.rows);
    setColumns(parsed.columns);
    setMapping(autoMapCommercialColumns(parsed.columns, nextDataset));
    setPreview(null);
    setExecution(null);
    setError(null);
  };

  const readFile = async file => {
    if (!file) return;
    setBusy(true);
    setError(null);
    try {
      const buffer = await file.arrayBuffer();
      const book = XLSX.read(buffer, { type: 'array', cellDates: true });
      if (!book.SheetNames?.length) throw new Error('The workbook has no sheets.');
      setWorkbook(book);
      setFilename(file.name);
      setSheetNames(book.SheetNames);
      selectSheet(book, book.SheetNames[0]);
    } catch (cause) {
      setWorkbook(null); setFilename(''); setSheetNames([]); setMatrix([]); setRows([]); setColumns([]); setMapping({});
      setError(new Error(`Could not read ${file.name}. ${cause.message || 'Check that it is a valid Excel or CSV file.'}`));
    } finally { setBusy(false); }
  };

  const changeDataset = nextDataset => {
    setDatasetCode(nextDataset);
    if (matrix.length) {
      const parsed = parseCommercialSheetMatrix(matrix, nextDataset);
      setHeaderRow(parsed.headerRow);
      setDetectedHeaderRow(parsed.detectedHeaderRow);
      setRows(parsed.rows);
      setColumns(parsed.columns);
      setMapping(autoMapCommercialColumns(parsed.columns, nextDataset));
    } else setMapping(autoMapCommercialColumns(columns, nextDataset));
    setPreview(null);
    setExecution(null);
    setError(null);
  };

  const requestBody = expectedFingerprint => buildCommercialImportRequest({
    dataset: datasetCode, filename, sheet, headerRow, mapping, rows, expectedFingerprint,
  });

  const changeHeaderRow = value => {
    const parsed = parseCommercialSheetMatrix(matrix, datasetCode, value);
    setHeaderRow(parsed.headerRow);
    setRows(parsed.rows);
    setColumns(parsed.columns);
    setMapping(autoMapCommercialColumns(parsed.columns, datasetCode));
    setPreview(null);
    setExecution(null);
    setError(null);
  };

  const validateRows = async () => {
    if (missing.length) return;
    setBusy(true); setError(null); setPreview(null);
    try {
      const response = await fetch(`${hostname}/commercial/imports/preview`, {
        method: 'POST', headers, body: JSON.stringify(requestBody()),
      });
      if (!response.ok) throw await responseError(response, 'Import validation failed');
      setPreview(await response.json());
      setStep(2);
    } catch (cause) { setError(cause); }
    finally { setBusy(false); }
  };

  const execute = async () => {
    if (!preview?.canExecute || execution) return;
    setBusy(true); setError(null);
    try {
      const response = await fetch(`${hostname}/commercial/imports/execute`, {
        method: 'POST', headers, body: JSON.stringify(requestBody(preview.fingerprint)),
      });
      if (!response.ok) throw await responseError(response, 'Import failed');
      const result = await response.json();
      await loadRecent();
      setExecution(result);
      if (onImported) onImported(result);
    } catch (cause) { setError(cause); }
    finally { setBusy(false); }
  };

  const restart = () => {
    setStep(0); setWorkbook(null); setFilename(''); setSheetNames([]); setSheet(''); setMatrix([]); setHeaderRow(1); setDetectedHeaderRow(1); setColumns([]);
    setRows([]); setMapping({}); setPreview(null); setExecution(null); setError(null);
  };

  return <>
    <Card className={classes.workflow}>
      <CardContent>
        <Stepper activeStep={step} alternativeLabel className={classes.stepper}>
          {steps.map(label => <Step key={label}><StepLabel>{label}</StepLabel></Step>)}
        </Stepper>

        {error && <Box className={`${classes.notice} ${classes.errorNotice}`} role="alert">
          <Typography variant="subtitle2">This step could not be completed</Typography>
          <Typography variant="body2">{error.message}</Typography>
        </Box>}

        {step === 0 && <Box className={classes.section}>
          <Typography component="h2" variant="h6" gutterBottom>Choose the records and source file</Typography>
          <Typography variant="body2" color="textSecondary" paragraph>Select the dataset, file and workbook sheet. Nothing is sent to the server at this step.</Typography>
          <div className={classes.controls}>
            <FormControl variant="outlined" style={{ minWidth: 220 }}>
              <InputLabel id="commercial-import-dataset-label">Dataset</InputLabel>
              <Select labelId="commercial-import-dataset-label" label="Dataset" value={datasetCode} onChange={event => changeDataset(event.target.value)}>
                {commercialImportDatasets.map(option => <MenuItem key={option.code} value={option.code}>{option.label}</MenuItem>)}
              </Select>
            </FormControl>
            <Button component="label" variant="contained" color="primary" startIcon={busy ? <CircularProgress size={18} color="inherit" /> : <CloudUploadIcon />} disabled={busy}>
              Choose Excel or CSV
              <input hidden type="file" accept=".xlsx,.xls,.csv" aria-label="Choose Excel or CSV file" onChange={event => readFile(event.target.files?.[0])} />
            </Button>
            {filename && <Typography variant="body2"><strong>{filename}</strong></Typography>}
          </div>
          {sheetNames.length > 0 && <Box mt={2} className={classes.controls}>
            <FormControl variant="outlined" style={{ minWidth: 260 }}>
              <InputLabel id="commercial-import-sheet-label">Workbook sheet</InputLabel>
              <Select labelId="commercial-import-sheet-label" label="Workbook sheet" value={sheet} onChange={event => selectSheet(workbook, event.target.value)}>
                {sheetNames.map(name => <MenuItem key={name} value={name}>{name}</MenuItem>)}
              </Select>
            </FormControl>
            <TextField
              type="number"
              variant="outlined"
              label="Header row"
              value={headerRow}
              inputProps={{ min: 1, max: Math.max(1, matrix.length), 'aria-label': 'Workbook header row' }}
              helperText={`Detected row ${detectedHeaderRow}; change this only if the column names are elsewhere.`}
              onChange={event => changeHeaderRow(event.target.value)}
              style={{ maxWidth: 220 }}
            />
            <Box>
              <Typography variant="body2" color="textSecondary">{rows.length} non-empty sheet {rows.length === 1 ? 'row' : 'rows'} detected / {columns.length} columns</Typography>
              <Typography variant="caption" color="textSecondary">Rows with values only in ignored columns are excluded after mapping.</Typography>
            </Box>
          </Box>}
          <Box className={classes.actions} justifyContent="flex-end">
            <Button variant="contained" color="primary" disabled={!rows.length || busy} onClick={() => setStep(1)}>Review column mapping</Button>
          </Box>
        </Box>}

        {step === 1 && <Box className={classes.section}>
          <Typography component="h2" variant="h6" gutterBottom>Review how columns will be interpreted</Typography>
          <Typography variant="body2" color="textSecondary" paragraph>Automatic matches are suggestions. Check each one; unmapped columns are ignored. The first 25 rows appear below.</Typography>
          {missing.length > 0 && <Box className={`${classes.notice} ${classes.errorNotice}`} role="alert">
            <Typography variant="subtitle2">Required mapping missing</Typography>
            <Typography variant="body2">Map {missing.map(field => dataset.fields.find(item => item.key === field)?.label || field).join(', ')} before validation.</Typography>
          </Box>}
          <div className={classes.mappingGrid}>
            {dataset.fields.map(field => <FormControl key={field.key} required={field.required} variant="outlined">
              <InputLabel id={`commercial-import-${field.key}-label`}>{field.label}{field.required ? ' *' : ''}</InputLabel>
              <Select
                labelId={`commercial-import-${field.key}-label`}
                aria-label={`Map ${field.label}`}
                label={`${field.label}${field.required ? ' *' : ''}`}
                value={mapping[field.key] || ''}
                onChange={event => { setMapping(current => ({ ...current, [field.key]: event.target.value })); setPreview(null); }}
              >
                <MenuItem value=""><em>Do not import</em></MenuItem>
                {columns.map(column => <MenuItem key={column} value={column}>{column}</MenuItem>)}
              </Select>
            </FormControl>)}
          </div>
          <TableContainer className={classes.table}>
            <Table size="small" stickyHeader className={classes.dataTable} aria-label="Mapped import rows">
              <caption>First 25 rows after applying the mapping above.</caption>
              <TableHead><TableRow><TableCell>Source row</TableCell>{Object.keys(mapping).filter(key => mapping[key]).map(key => <TableCell key={key}>{dataset.fields.find(field => field.key === key)?.label || key}</TableCell>)}</TableRow></TableHead>
              <TableBody>{mappedRows.map(row => <TableRow key={row.sourceRow}><TableCell>{row.sourceRow}</TableCell>{Object.keys(mapping).filter(key => mapping[key]).map(key => <TableCell key={key}>{String(row[key] ?? '') || '-'}</TableCell>)}</TableRow>)}</TableBody>
            </Table>
          </TableContainer>
          <Box className={classes.actions} justifyContent="space-between">
            <Button startIcon={<ArrowBackIcon />} onClick={() => setStep(0)}>Back to source</Button>
            <Button variant="contained" color="primary" disabled={busy || missing.length > 0} onClick={validateRows}>{busy ? 'Validating...' : 'Validate with server'}</Button>
          </Box>
        </Box>}

        {step === 2 && preview && <Box className={classes.section}>
          {execution ? <>
            <Box className={`${classes.notice} ${execution.status === 'FAILED' ? classes.errorNotice : classes.successNotice}`} role="status">
              <Typography component="h2" variant="h6">{execution.status === 'FAILED' ? 'Import recorded with no changes' : 'Import complete'}</Typography>
              <Typography variant="body2">{execution.replayed ? 'This exact dataset was already imported, so the saved result was returned without repeating changes.' : 'The server saved this batch and its validation history.'}</Typography>
              <SummaryChips summary={execution.summary} />
            </Box>
            <IssueTable issues={execution.issues} />
            <Box className={classes.actions}>
              <Button startIcon={<RefreshIcon />} onClick={restart}>Import another file</Button>
              <Button color="primary" variant="contained" href={datasetCode === 'CUSTOMERS' ? '#/commercial-customers' : '#/commercial-sales'}>View {dataset.label.toLowerCase()}</Button>
            </Box>
          </> : <>
            <Box className={`${classes.notice} ${partialPreview ? classes.warningNotice : preview.canExecute ? classes.successNotice : classes.errorNotice}`} role="status">
              <Typography component="h2" variant="h6">{partialPreview ? 'Partial import ready' : preview.canExecute ? 'Ready to import' : 'Changes required before import'}</Typography>
              <Typography variant="body2">{partialPreview
                ? `Server validation found ${preview.totalRows} mapped source rows. ${previewValidRows} valid ${previewValidRows === 1 ? 'row can' : 'rows can'} be applied; ${previewFailedRows} rejected ${previewFailedRows === 1 ? 'row will' : 'rows will'} remain unchanged. Confirming applies the exact fingerprint reviewed here.`
                : `Server validation found ${preview.totalRows} mapped source rows. Applying the import creates or updates only the rows summarized here. The reviewed fingerprint prevents a replay from duplicating data.`}</Typography>
              <SummaryChips summary={preview.summary} />
            </Box>
            <IssueTable issues={preview.issues} />
            <Box className={classes.actions} justifyContent="space-between">
              <Button startIcon={<ArrowBackIcon />} onClick={() => { setPreview(null); setStep(1); }}>Back and change mapping</Button>
              <Button startIcon={busy ? <CircularProgress size={18} color="inherit" /> : <DoneIcon />} variant="contained" color="primary" disabled={busy || !preview.canExecute} onClick={execute}>{busy ? 'Importing...' : partialPreview ? `Apply ${previewValidRows} valid ${previewValidRows === 1 ? 'row' : 'rows'}` : 'Apply import'}</Button>
            </Box>
          </>}
        </Box>}
      </CardContent>
    </Card>

    <Card className={classes.supporting}>
      <CardContent>
        <Typography component="h2" variant="h6">Records managed outside bulk import</Typography>
        <Typography variant="body2" color="textSecondary">These records have financial or lifecycle side effects. Use their existing workflow until a safe bulk contract is available.</Typography>
        <div className={classes.deferredList}>{deferredCommercialImports.map(item => <div key={item.label} className={classes.deferredItem}>
          <Typography variant="subtitle2">{item.label}</Typography>
          <Typography variant="body2" color="textSecondary">{item.reason}</Typography>
          <Link href={item.href} aria-label={`Open ${item.destination} for ${item.label}`}>Open {item.destination}</Link>
        </div>)}</div>
      </CardContent>
    </Card>

    {recent.length > 0 && <Card className={classes.supporting}>
      <CardContent>
        <Typography component="h2" variant="h6">Recent imports</Typography>
        <TableContainer><Table size="small" aria-label="Recent commercial imports">
          <TableHead><TableRow><TableCell>File</TableCell><TableCell>Dataset / sheet</TableCell><TableCell>Status</TableCell><TableCell>Result</TableCell></TableRow></TableHead>
          <TableBody>{recent.map(batch => <TableRow key={batch.id}>
            <TableCell>{batch.filename}</TableCell>
            <TableCell>{batch.summary?.dataset || '-'} / {batch.summary?.sheet || '-'}</TableCell>
            <TableCell><Chip size="small" variant="outlined" label={String(batch.status || 'UNKNOWN').replace(/_/g, ' ')} /></TableCell>
            <TableCell>{Number(batch.summary?.created || 0)} created / {Number(batch.summary?.updated || 0)} updated / {Number(batch.summary?.skipped || 0)} skipped / {Number(batch.summary?.failed || 0)} failed</TableCell>
          </TableRow>)}</TableBody>
        </Table></TableContainer>
      </CardContent>
    </Card>}
  </>;
}
